import has from "lodash/has";
import isArray from "lodash/isArray";
import isNil from "lodash/isNil";
import map from "lodash/map";
import { ChangelogItem } from "PFApp/components/changelog/components/changelog_item";
import { ElementPill } from "PFApp/components/changelog/components/element_pill";
import { LoadingDots } from "PFComponents/loading_dots/loading_dots";
import { Pill } from "PFComponents/pill";
import { GenericActivityEvent } from "PFTypes";
import { useTranslation } from "react-i18next";

import { useSanitizedAttributes } from "../sanitize_history_entry";
import { HistoryChangeSection } from "./history_change_section";

const renderValue = (value?: string | number | boolean | null) => (isNil(value) ? "-" : String(value));

type HistoryUpdatedAttributesProps = {
  level?: number;
  label?: string;
  updatedAttributes: GenericActivityEvent["diff"];
  metadata: GenericActivityEvent["metadata"];
};

export const HistoryUpdatedAttributes = ({
  label,
  level = 0,
  updatedAttributes,
  metadata
}: HistoryUpdatedAttributesProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.history.entry.updatedAttributes" });

  const isValueRemoved = has(updatedAttributes, "id") && updatedAttributes.id![1] === null;
  const isValueAdded = has(updatedAttributes, "id") && updatedAttributes.id![0] === null;
  const isLowestLevel = isArray(updatedAttributes);
  const { sanitizedAttributes, isFetching } = useSanitizedAttributes(updatedAttributes, metadata, label);

  const renderUpdatedAttributes = (attributes: GenericActivityEvent["diff"]) =>
    map(attributes, (attribute: GenericActivityEvent["diff"], key: string) => (
      <HistoryUpdatedAttributes
        key={key}
        label={key}
        updatedAttributes={attribute}
        level={level + 1}
        metadata={metadata}
      />
    ));

  return (
    <HistoryChangeSection labelKey={label} level={level}>
      {isValueAdded && (
        <Pill kind="success" small>
          {t("added")}
        </Pill>
      )}
      {isValueRemoved && (
        <Pill kind="danger" small>
          {t("removed")}
        </Pill>
      )}
      {isFetching ? (
        <LoadingDots />
      ) : !isLowestLevel ? (
        !isValueRemoved && renderUpdatedAttributes(sanitizedAttributes)
      ) : (
        <ChangelogItem
          before={<ElementPill>{renderValue(sanitizedAttributes[0])}</ElementPill>}
          after={<ElementPill highlighted>{renderValue(sanitizedAttributes[1])}</ElementPill>}
        />
      )}
    </HistoryChangeSection>
  );
};
